<template>
  <div 
    ref="containerRef"
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada || showQRCode }"
      ref="formCardRef"
    >
      <!-- Vista de QR confirmado -->
      <div
        v-if="asistenciaConfirmada || showQRCode"
        class="qr-view text-center"
        ref="qrViewRef"
      >
        <p class="qr-header">Pase Digital</p>
        <div class="qr-wrapper">
          <img v-if="qrCode" :src="qrCode" alt="Código QR" class="qr-code">
          <p v-else class="qr-error-message">No se ha generado el código QR</p>
        </div>
        <p class="qr-instructions">Preséntalo el día del evento</p>
      </div>

      <!-- Formulario paso a paso -->
      <div v-else>
        <div class="form-header">
<h2 ref="tituloAnimado" class="form-title">
            Confirma<br><span class="subtitulo">Asistencia</span>
          </h2>
          <br>
          <br>

          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
            ></div>
          </div>
          <p class="form-subtitle">Paso {{ currentStep }} de {{ totalSteps }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Paso 1: Nombre y Teléfono -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label for="name" class="form-label">Nombre</label>
              <input
                id="name"
                v-model.trim="name"
                type="text"
                class="form-control"
                placeholder="Ingresa tu nombre completo"
                required
              >
            </div>
            <div class="form-group">
              <label for="phone" class="form-label">Teléfono</label>
              <input
                id="phone"
                v-model.trim="phone"
                type="tel"
                class="form-control"
                placeholder="Ingresa tu teléfono"
                required
                pattern="[0-9]{10}"
                title="Ingresa un número de 10 dígitos"
              >
            </div>
          </div>

          <!-- Paso 2: Invitado por (oculto en XV o YM) -->
          <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
            <div class="form-group">
              <label class="form-label">¿Invitado por?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required>
                  <label for="novia" class="radio-label">Novia</label>
                </div>
                <div class="radio-option">
                  <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input">
                  <label for="novio" class="radio-label">Novio</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 3: Asistencia -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <div class="form-group">
              <label class="form-label">¿Asistirás?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
                  <label for="si" class="radio-label">Sí</label>
                </div>
                <div class="radio-option">
                  <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
                  <label for="no" class="radio-label">No</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 4: Número de invitados -->
          <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <div class="form-group">
              <label for="guests" class="form-label">¿Cuántos asisten?</label>
              <select
                id="guests"
                v-model.number="guests"
                class="form-select"
                :disabled="attending === 'no' || loading"
                required
              >
                <option :value="null" disabled>Selecciona una opción</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
              </select>
              <div v-if="loading" class="loading-indicator">Cargando opciones...</div>
            </div>
          </div>

          <!-- Botones de navegación -->
          <div class="d-flex justify-content-between mt-3 buttons-row">
            <button type="button" class="submit-button secondary" @click="prevStep" v-if="currentStep > 1">
              Atrás
            </button>
            <button
              v-if="currentStep < totalSteps"
              type="button"
              class="submit-button"
              @click="nextStep"
            >
              Siguiente
            </button>
            <button
              v-if="currentStep === totalSteps"
              type="submit"
              class="submit-button"
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">Confirmar</span>
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
        </form>
      </div>
    </div>

    <div v-show="showTooltip" ref="tooltipRef" class="tooltip-reminder">
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '@/api';
import gsap from 'gsap';
import Swal from 'sweetalert2';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true, validator: v => Number.isInteger(v) && v > 0 },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');
    const guests = ref(null);
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');

    const tituloAnimado = ref(null);

    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    const currentStep = ref(1);
    const formCardRef = ref(null);
    const containerRef = ref(null);
    const qrViewRef = ref(null);

    const showTooltip = ref(false);
    const tooltipRef = ref(null);

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4);
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3);

    const animateStep = () => {
      nextTick(() => {
        const box = formCardRef.value?.querySelector('.step-box');
        if (!box) return;
        gsap.fromTo(
          box,
          { y: 40, opacity: 0, filter: 'blur(6px)' },
          { y: 0, opacity: 1, filter: 'blur(0px)', duration: 0.6, ease: 'power2.out' }
        );
      });
    };

    const nextStep = async () => {
      if (currentStep.value === 1) {
        if (!name.value.trim() || !phone.value.trim()) {
          errorMessage.value = 'Por favor completa tu nombre y teléfono.';
          return;
        }
      }

      if (currentStep.value === 2 && !isXvEvent.value) {
        if (!invitedBy.value) {
          errorMessage.value = 'Selecciona quién te invitó.';
          return;
        }
      }

      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Por favor selecciona si asistirás o no.';
          return;
        }
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value;
          await nextTick();
          handleSubmit();
          return;
        }
      }

      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (guests.value == null || Number.isNaN(Number(guests.value))) {
          errorMessage.value = 'Por favor selecciona cuántas personas asistirán.';
          return;
        }
      }

      errorMessage.value = '';
      if (currentStep.value < totalSteps.value) {
        currentStep.value++;
        animateStep();
      }
    };

    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        animateStep();
      }
    };

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(`${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`);
        const numPases = Number(response.data.num_pase);
        guestsOptions.value = numPases > 0
          ? Array.from({ length: numPases }, (_, i) => i + 1)
          : [1, 2];
      } catch (error) {
        console.error('Error al obtener límite de invitados:', error);
        errorMessage.value = 'Error al cargar las opciones de invitados';
      } finally {
        loading.value = false;
      }
    };

    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: props.idInvitado
        });
        if (response.data.asistio) {
          asistenciaConfirmada.value = true;
          await obtenerCodigoQR();
        }
      } catch (error) {
        console.error('Error al verificar asistencia:', error);
      }
    };

    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, { id_invitado: Number(props.idInvitado) });
        qrCode.value = response.data?.codigoqr || null;
      } catch (error) {
        console.error('Error obteniendo código QR:', error);
        errorMessage.value = 'Error al generar el código QR';
      }
    };

    const handleSubmit = async () => {
      if (attending.value === 'no') guests.value = 0;

      isSubmitting.value = true;
      errorMessage.value = '';

      try {
        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado,
          num_pases: Number(attending.value === 'no' ? 0 : (guests.value ?? 0)),
        };

        await axios.post(`${baseUrl}/asistencia`, formData);

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: '¡Gracias por avisarnos!',
            text: 'Sentimos mucho que no puedas asistir.',
            confirmButtonColor: '#d88cbd',
          });
          asistenciaConfirmada.value = true;
        } else {
          await obtenerCodigoQR();
          showQRCode.value = true;
        }
      } catch (error) {
        console.error('Error confirmando asistencia:', error);
        errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.';
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = 0;
      } else if (newValue === 'si' && (guests.value === 0 || guests.value == null)) {
        guests.value = 1;
      }
    });

    // Animar QR cuando aparece
    watch([asistenciaConfirmada, showQRCode], async ([confirmada, qr]) => {
      if (!confirmada && !qr) return;
      await nextTick();
      if (qrViewRef.value) {
        gsap.fromTo(
          qrViewRef.value,
          { opacity: 0, y: 40, scale: 0.96 },
          { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'power2.out' }
        );
        const qrImg = qrViewRef.value.querySelector('.qr-code');
        if (qrImg) {
          gsap.fromTo(
            qrImg,
            { scale: 0.6, opacity: 0, rotate: -6 },
            { scale: 1, opacity: 1, rotate: 0, duration: 1.1, ease: 'elastic.out(1, 0.5)' }
          );
        }
      }
    });

    onMounted(() => {
      fetchGuestLimit();
      verificarAsistencia();
      animateStep();

      // Animación de entrada de la tarjeta al entrar en viewport
      if (formCardRef.value) {
        gsap.fromTo(
          formCardRef.value,
          {
            opacity: 0,
            y: 60,
            scale: 0.96,
            filter: 'blur(10px)'
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: 1.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: formCardRef.value,
              start: 'top 85%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      }

      // Título con animación suave
      if (tituloAnimado.value) {
        gsap.fromTo(
          tituloAnimado.value,
          { y: -40, opacity: 0, scale: 1.05 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 1,
            ease: 'back.out(1.4)',
            scrollTrigger: {
              trigger: formCardRef.value || tituloAnimado.value,
              start: 'top 85%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      }

      // Tooltip que aparece flotando al cabo de unos segundos (opcional)
      setTimeout(() => {
        showTooltip.value = true;
        if (tooltipRef.value) {
          gsap.fromTo(
            tooltipRef.value,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
          );
          gsap.to(tooltipRef.value, {
            y: '+=8',
            duration: 1.8,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut'
          });
        }
      }, 3500);
    });

    return {
      name, phone, invitedBy, attending, guests, guestsOptions,
      loading, isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada,
      qrCode, showQRCode, currentStep, nextStep, prevStep, handleSubmit,
      totalSteps, asistenciaStep, formCardRef, tituloAnimado,
      containerRef, qrViewRef, showTooltip, tooltipRef
    };
  }
};
</script>

<style scoped>
/* Estilos base */
.confirmation-container {
  min-height: 70vh;
  padding: 2rem 1rem;

  transition: all 0.3s ease;
}

.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: white;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

/* Vista compacta para QR */
.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

/* Encabezado del formulario */
.form-header {

  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
  font-family: 'CynthiaJuneJF', cursive;
  font-size: 46px !important;
  background: linear-gradient(110deg, #a67c00 30%, #bf953f 45%, #ffffff 50%, #bf953f 55%, #a67c00 70%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0px 2px 3px rgba(0,0,0,0.3));
  display: block; 
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
}

/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  color: #495057;
}

/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus,
.form-select:focus {
  border-color: #bf953f;
  box-shadow: 0 0 0 0.25rem rgba(213, 146, 176, 0.25);
  outline: none;
}

/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #bf953f;
}

.radio-label {
  font-family: 'Karla', sans-serif;
  color: #495057;
}

/* Botón de enviar */
.submit-button {
  width: 100%;
  padding: 0.75rem;
  font-family: 'FeelingPassionate', cursive;
  font-size: 1.35rem;
 /* Imagen Dorada */
  background-image: url('@/assets/dorado.jpeg');
  background-size: cover;
  background-position: center;
  background-color: #bf953f; /* Respaldo */

  /* Texto */
  color: #f7f6f6 !important;
  -webkit-text-fill-color: #ffffff !important;
  text-decoration: none;  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover {
  background-image: url('@/assets/dorado.jpeg');
}

.submit-button:disabled {
  background-image: url('@/assets/dorado.jpeg');
  opacity: 0.7;
  cursor: not-allowed;
}

/* Vista QR */
.qr-view {
  padding: 1rem;
}

.qr-header {
  font-family: 'Westony', cursive;
  font-size: 1.75rem;
  color: #5a3d5c;
  margin-bottom: 1.5rem;
}

.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #354257;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.qr-wrapper {
  margin: 1rem 0;
}

.qr-code {
  width: 180px;
  height: 180px;
  border: 8px solid white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.decoration-crown {
  width: auto;
  height: 80px;
  margin: 0.5rem 0;
}

.event-title {
  font-family: 'Parisienne', cursive;
  font-size: 1.75rem;
  color: #5a3d5c;
  margin: 0.5rem 0;
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
  margin-top: 1rem;
}

/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.qr-error-message {
  color: #dc3545;
  font-weight: 500;
}

.loading-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
  
  .radio-group {
    gap: 1rem;
  }
}




.progress-container {
  height: 8px;
  margin-top:40px;
  background-color: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #bf953f; /* color rosado bonito */
  transition: width 0.4s ease;
}

</style>